import clsx from 'clsx'
import { ReactNode } from 'react'
import AddressWithHeading, {
  AddressWithHeadingProps,
} from '../AddressWithHeading/AddressWithHeading'
import Icon from '../Icon/Icon'

export interface OriginDestinationAddressProps {
  /**
   * The origin address
   */
  origin?: AddressWithHeadingProps['addressLines']
  /**
   * Origin Heading
   */
  originHeader?: string
  /**
   * Origin Subheading
   */
  originSubHeading?: string
  /**
   * The destination address
   */
  destination?: AddressWithHeadingProps['addressLines']
  /**
   * Destination Heading
   */
  destinationHeader?: string
  /**
   * Destination Subheading
   */
  destinationSubHeading?: string
  /**
   * If location icon is displayed
   */
  hasIcon?: boolean
  /**
   * Optional content rendered directly beneath the origin address
   * (e.g. cruise details when the origin end is a cruise, i.e. a return leg).
   */
  originFooter?: ReactNode
  /**
   * Optional content rendered directly beneath the destination address
   * (e.g. cruise details when the destination end is a cruise, i.e. an outbound leg).
   */
  destinationFooter?: ReactNode
  /**
   * Optional additional class name
   */
  className?: string
}

export const formatAddressLines = (
  address: OrderAddress,
  hasPhoneNumber?: boolean,
) =>
  [
    [address.company_name, address.address_2].filter(Boolean).join(', '),
    ...(hasPhoneNumber ? [address.phone_number] : []),
    address.address_1,
    [
      address.city,
      [address.state, address.zip, address.country_code?.toUpperCase()]
        .filter(Boolean)
        .join(' '),
    ].join(', '),
  ].filter(Boolean)

export default function OriginDestinationAddress({
  origin,
  originHeader = 'Origin',
  originSubHeading,
  destination,
  destinationHeader = 'Destination',
  destinationSubHeading,
  hasIcon = true,
  originFooter,
  destinationFooter,
  className,
}: OriginDestinationAddressProps) {
  return (
    <div
      className={clsx(
        'flex flex-col items-start gap-y-4 md:flex-row md:items-start md:justify-between w-full',
        'md:pt-6 md:border-t md:border-t-neutral-400',
        className,
      )}
    >
      {origin && (
        <div className="flex flex-col items-start gap-2 md:mr-auto">
          <AddressWithHeading
            heading={originHeader}
            subHeading={originSubHeading}
            addressLines={origin}
            hasIcon={hasIcon}
          />
          {originFooter}
        </div>
      )}

      {/* The layout stacks below `md` and goes side-by-side above it, so the
          arrow has to turn with it — same pair ShipmentDetailsModalRoute uses.
          Previously only the right-pointing arrow existed: it was hidden below
          `sm`, leaving no direction cue on mobile, and between `sm` and `md` it
          pointed sideways across a vertical stack. */}
      <Icon
        icon="arrow-down"
        className="w-6 h-6 md:hidden text-theme-accessibility"
      />
      <Icon
        icon="arrow-right"
        className="w-6 h-6 md-max:hidden md:mx-4 text-theme-accessibility"
      />
      {destination && (
        <div className="flex flex-col items-start gap-2 md:ml-auto">
          <AddressWithHeading
            heading={destinationHeader}
            subHeading={destinationSubHeading}
            addressLines={destination}
            hasIcon={hasIcon}
          />
          {destinationFooter}
        </div>
      )}
    </div>
  )
}
